import React, { useState } from 'react';
import { useI18n } from '../context/I18nContext';
import { Link } from 'react-router-dom';
import { LogoIcon, TrashIcon, CheckIcon, AlertIcon } from '../components/Icons';
import CookieConsent from '../components/CookieConsent';
import { useSettings } from '../context/SettingsContext';

const DataDeletionRequest = () => {
  const { settings } = useSettings();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!email || !confirmEmail) {
      setError('Preencha todos os campos obrigatorios.');
      return;
    }

    if (email !== confirmEmail) {
      setError(t('delreq.emailMismatch'));
      return;
    }

    // Simula envio da solicitacao
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <LogoIcon className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-gray-900">{settings.companyName}</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            <span className="text-sm font-medium">{t('delreq.backHome')}</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('datadeleti.j1')}</h1>
          <p className="text-gray-600">{t('datadeleti.j2')}</p>
        </div>

        {submitted ? (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckIcon className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">
              Solicitacao Recebida
            </h2>
            <p className="text-green-700 mb-4">{t('datadeleti.j3')}</p>
            <p className="text-sm text-green-600 mb-6">
              Protocolo: DEL-{Date.now().toString(36).toUpperCase()}
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-all"
            >{t('datadeleti.j4')}</Link>
          </div>
        ) : (
          <>
            {/* Informacoes */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">{t('datadeleti.j5')}</h2>

              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 text-blue-600 font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('delreq.sendRequest')}</h3>
                    <p className="text-gray-600 text-sm">{t('datadeleti.j6')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 text-blue-600 font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('delreq.identityCheck')}</h3>
                    <p className="text-gray-600 text-sm">{t('datadeleti.j7')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 text-blue-600 font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('delreq.processing')}</h3>
                    <p className="text-gray-600 text-sm">{t('datadeleti.j8')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 text-blue-600 font-bold">
                    4
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{t('delreq.confirmation')}</h3>
                    <p className="text-gray-600 text-sm">{t('datadeleti.j9')}</p>
                  </div>
                </div>
              </div>

              {/* Aviso */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-amber-800 mb-1">{t('delreq.whatIsDeleted')}</h4>
                    <ul className="text-amber-700 text-sm space-y-1 list-disc list-inside">
                      <li>{t('delreq.profileInfo')}</li>
                      <li>{t('delreq.waConnections')}</li>
                      <li>{t('del.messageHistory')}</li>
                      <li>{t('del.settings')}</li>
                      <li>{t('del.metaStored')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Formulario */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">{t('datadeleti.j10')}</h2>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
                  <AlertIcon className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">{t('datadeleti.j11')}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('contact.emailPlaceholder')}
                    className="w-full h-12 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">{t('datadeleti.j12')}</label>
                  <input
                    type="email"
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    placeholder={t('contact.emailPlaceholder')}
                    className="w-full h-12 px-4 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">{t('datadeleti.j13')}</label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={t('delreq.why')}
                    rows={4}
                    className="w-full px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-none"
                  />
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 h-12 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all"
                  >
                    <TrashIcon className="w-5 h-5" />{t('del.request')}</button>
                </div>
              </form>
            </div>

            {/* Usuarios logados */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <h3 className="font-semibold text-blue-800 mb-2">{t('datadeleti.j14')}</h3>
              <p className="text-blue-700 text-sm mb-4">{t('delreq.haveAccount', { v1: settings.companyName })}</p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all text-sm"
              >
                Fazer login
              </Link>
            </div>
          </>
        )}

        {/* Contato */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-2">{t('datadeleti.j15')}</p>
          {(settings.dpoEmail || settings.email) ? (
            <p className="text-gray-900">{t('datadeleti.j16')}<a href={`mailto:${settings.dpoEmail || settings.email}`} className="text-blue-600 hover:underline">{settings.dpoEmail || settings.email}</a>
            </p>
          ) : (
            <p className="text-gray-500 text-sm">{t('datadeleti.j17')}</p>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 mt-12">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {settings.companyName}. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacidade" className="text-gray-500 hover:text-gray-900 text-sm">{t('datadeleti.j18')}</Link>
              <Link to="/termos" className="text-gray-500 hover:text-gray-900 text-sm">{t('datadeleti.j19')}</Link>
            </div>
          </div>
        </div>
      </footer>

      <CookieConsent />
    </div>
  );
};

export default DataDeletionRequest;
